import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import styles from 'assets/jss/material-kit-react/views/profilePage.js';
import NavPills from 'components/NavPills/NavPills.js';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import BubbleContainer from 'components/BubbleContainer/BubbleContainer';
import WorkWithUsForm from 'components/WorkWithUs/WorkWithUsForm';
import d2ricon from 'assets/img/d2r-logo.png';
import d2rsicon from 'assets/img/d2rsiconblock.png';
import {
  ConstructionOutlined,
  Diversity1Outlined,
  HandshakeOutlined,
  PersonAddAlt1Outlined,
} from '@mui/icons-material';
import poe2icon from 'assets/img/poe2-logo.webp';
import GraphicDesignerContent from 'components/WorkWithUs/GraphicDesignerContent';
import ToolsDevsContent from 'components/WorkWithUs/ToolsDevsContent';
import CommunityContent from 'components/WorkWithUs/ContentCreatorContent';
import Card from 'components/Card/Card.js';
import CardHeader from 'components/Card/CardHeader.js';
import CardBody from 'components/Card/CardBody.js';

const useStyles = makeStyles<Theme>((theme) => ({
  mainContainer: {
    position: 'relative',
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    borderRadius: '6px',
    color: theme.palette.text.primary,
  },
  container: {
    ...styles.container,
    position: 'relative',
    zIndex: 3,
  },
  contentContainer: {
    marginLeft: 'auto',
    marginRight: 'auto',
    textAlign: 'center',
    zIndex: 1100,
    '& .MuiTabs-root': {
      zIndex: 1200,
    },
    '& .MuiTab-root': {
      zIndex: 1200,
    },
    '& .MuiTabs-root + div': {
      marginTop: 0,
    },
    '& .MuiTabs-root + div > div': {
      marginTop: 0,
    },
    '& .contentWrapper': {
      marginTop: 0,
    },
  },
  gameIcon: {
    width: '48px',
    verticalAlign: 'middle',
    marginLeft: '8px',
  },
  tabButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tabIcon: {
    fontSize: 16,
    verticalAlign: 'middle',
    marginLeft: '8px',
    color:
      theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.primary.main,
  },
}));

export default function WorkWithUs() {
  const classes = useStyles();
  const { t } = useTranslation();
  useDocumentTitle(t('navigation.workWithUsTitle'));

  return (
    <div>
      <div className={classes.mainContainer}>
        <div className={classes.container}>
          <div className={classes.contentContainer}>
            <Card>
              <CardHeader color="primary">
                <h4>{t('navigation.workWithUsTitle')}</h4>
              </CardHeader>
              <CardBody>
                <NavPills
                  queryKey={'t'}
                  alignCenter
                  fullWidth
                  onChange={function () {}}
                  color="primary"
                  tabs={[
                    {
                      tabName: 'graphic',
                      tabButton: (
                        <div className={classes.tabButton}>
                          {t('workWithUs.graphicDesigner.pillTitle')}
                          <PersonAddAlt1Outlined className={classes.tabIcon} />
                          <img src={d2rsicon} alt="D2RS" className={classes.gameIcon} />
                        </div>
                      ),
                      tabContent: <GraphicDesignerContent />,
                    },
                    {
                      tabName: 'tools',
                      tabButton: (
                        <div className={classes.tabButton}>
                          {t('workWithUs.toolsDevs.pillTitle')}
                          <ConstructionOutlined className={classes.tabIcon} />
                          <img src={d2ricon} alt="D2R" className={classes.gameIcon} />
                          <img src={poe2icon} alt="POE2" className={classes.gameIcon} />
                        </div>
                      ),
                      tabContent: <ToolsDevsContent />,
                    },
                    {
                      tabName: 'community',
                      tabButton: (
                        <div className={classes.tabButton}>
                          {t('workWithUs.community.pillTitle')}
                          <Diversity1Outlined className={classes.tabIcon} />
                          <img src={d2ricon} alt="D2R" className={classes.gameIcon} />
                          <img src={poe2icon} alt="POE2" className={classes.gameIcon} />
                        </div>
                      ),
                      tabContent: <CommunityContent />,
                    },
                    {
                      tabName: 'apply',
                      tabButton: (
                        <div className={classes.tabButton}>
                          {t('navigation.workWithUs.pillTitle')}
                          <HandshakeOutlined className={classes.tabIcon} />
                        </div>
                      ),
                      tabContent: <WorkWithUsForm />,
                    },
                  ]}
                />
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
      <BubbleContainer pageKey="workWithUs" helpText={t('help.workWithUs')} />
    </div>
  );
}
